/**
 * Bot Error Helper
 * Provides error categorization and recovery suggestions
 */

export interface ErrorSuggestion {
  category: string
  description: string
  suggestions: string[]
  canRetry: boolean
  actionLabel?: string
}

/**
 * Categorize bot errors and provide recovery suggestions
 */
export function getBotErrorSuggestion(errorMessage: string): ErrorSuggestion {
  const lowerError = errorMessage.toLowerCase()

  // Currency Not Supported (New: Issue #2 fix)
  if (
    lowerError.includes('symbol: invalid') ||
    lowerError.includes('不支援借貸') ||
    lowerError.includes('不支援 bitfinex 借貸') ||
    lowerError.includes('currency not supported') ||
    lowerError.includes('usdt') && lowerError.includes('不支援')
  ) {
    return {
      category: '貨幣不支援',
      description: '此 Bot 使用的貨幣不支援 Bitfinex 借貸市場',
      suggestions: [
        '刪除此 Bot',
        '重新創建 Bot 並選擇支援的貨幣：USD, BTC, ETH, UST 或 TESTUSDT',
        '注意：USDT (Tether) 不支援借貸，請改用 USD 或 UST (Terra)',
      ],
      canRetry: false,
      actionLabel: '了解',
    }
  }

  // API Authentication Errors
  if (
    lowerError.includes('authentication') ||
    lowerError.includes('api key') ||
    lowerError.includes('invalid key') ||
    lowerError.includes('unauthorized')
  ) {
    return {
      category: 'API 認證錯誤',
      description: 'API Key 無效或已過期',
      suggestions: [
        '檢查 Bitfinex API Key 是否正確',
        '確認 API Key 權限包含「Funding」交易',
        '在 Bitfinex 重新生成 API Key',
        '更新 Bot 使用的 API Key',
      ],
      canRetry: false,
      actionLabel: '更新 API Key',
    }
  }

  // Insufficient Balance
  if (
    lowerError.includes('insufficient') ||
    lowerError.includes('balance') ||
    lowerError.includes('not enough') ||
    lowerError.includes('餘額不足')
  ) {
    return {
      category: '餘額不足',
      description: '帳戶可用餘額不足以執行放貸',
      suggestions: [
        '充值更多資金到 Funding 錢包',
        '降低 Bot 的最小放貸金額 (min_loan_size)',
        '減少 Bot 的分散筆數 (spread_lend)',
        '檢查是否有其他 Bot 佔用資金',
      ],
      canRetry: true,
      actionLabel: '重新啟動',
    }
  }

  // Rate Limit
  if (
    lowerError.includes('rate limit') ||
    lowerError.includes('too many requests') ||
    lowerError.includes('請求過多')
  ) {
    return {
      category: 'API 請求限制',
      description: '超過 Bitfinex API 請求頻率限制',
      suggestions: [
        '等待 1-2 分鐘後重試',
        '增加 Bot 的更新間隔 (update_interval)',
        '減少 Bot 的分散筆數以降低 API 請求',
        '檢查是否有多個 Bot 使用同一 API Key',
      ],
      canRetry: true,
      actionLabel: '稍後重試',
    }
  }

  // Network Errors
  if (
    lowerError.includes('network') ||
    lowerError.includes('connection') ||
    lowerError.includes('timeout') ||
    lowerError.includes('連線')
  ) {
    return {
      category: '網路連線錯誤',
      description: '無法連接到 Bitfinex 伺服器',
      suggestions: [
        '檢查網路連線是否正常',
        '確認 Bitfinex 服務狀態',
        '稍後重試',
        '檢查防火牆或 VPN 設定',
      ],
      canRetry: true,
      actionLabel: '重新啟動',
    }
  }

  // Order Rejection
  if (
    lowerError.includes('order') ||
    lowerError.includes('rejected') ||
    lowerError.includes('invalid rate') ||
    lowerError.includes('invalid amount')
  ) {
    return {
      category: '訂單被拒絕',
      description: '放貸訂單參數不符合 Bitfinex 要求',
      suggestions: [
        '檢查最低日利率是否過低 (建議至少 0.001%)',
        '確認最小放貸金額符合要求 (USD/USDT 建議 ≥ 50)',
        '調整 Gap 範圍以獲取更合理的利率',
        '查看 Bot 日誌了解詳細錯誤',
      ],
      canRetry: false,
      actionLabel: '修改配置',
    }
  }

  // Workflow Errors (Cloudflare specific)
  if (
    lowerError.includes('workflow') ||
    lowerError.includes('worker') ||
    lowerError.includes('execution')
  ) {
    return {
      category: '執行引擎錯誤',
      description: 'Bot 執行流程異常',
      suggestions: [
        '重新啟動 Bot',
        '檢查系統狀態',
        '如果問題持續，請聯繫技術支援',
      ],
      canRetry: true,
      actionLabel: '重新啟動',
    }
  }

  // Generic Error
  return {
    category: '未知錯誤',
    description: errorMessage,
    suggestions: [
      '重新啟動 Bot',
      '檢查 Bot 配置是否正確',
      '查看 Bot 日誌了解詳細資訊',
      '如果問題持續，請聯繫技術支援',
    ],
    canRetry: true,
    actionLabel: '重新啟動',
  }
}

/**
 * Get error severity level
 */
export function getErrorSeverity(errorMessage: string): 'critical' | 'warning' | 'info' {
  const lowerError = errorMessage.toLowerCase()

  // Critical errors that require immediate attention
  if (
    lowerError.includes('symbol: invalid') ||
    lowerError.includes('不支援借貸') ||
    lowerError.includes('不支援 bitfinex 借貸') ||
    lowerError.includes('currency not supported') ||
    lowerError.includes('authentication') ||
    lowerError.includes('api key') ||
    lowerError.includes('unauthorized')
  ) {
    return 'critical'
  }

  // Warnings that may resolve themselves
  if (
    lowerError.includes('rate limit') ||
    lowerError.includes('network') ||
    lowerError.includes('timeout')
  ) {
    return 'warning'
  }

  return 'info'
}
